import * as React from 'react';
import {
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  ListSubheader,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const ITEM_HEIGHT = 32;
const ITEM_PADDING_TOP = 2;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP,
      width: 180,
      overflowY: 'auto',
    },
  },
  autoFocus: false,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  getContentAnchorEl: null,
};

const useStyles = makeStyles(() => ({
  formControl: (props) => ({
    width: props.width,
  }),

  input: {
    '& .MuiOutlinedInput-input': {
      padding: '2px 6px',
      fontSize: '12px',
      height: props => props.height,
      lineHeight: 1.3,
    },
  },

  menuItem: {
    fontSize: '12px',
    paddingTop: 3,
    paddingBottom: 3,
  },

  placeholder: {
    color: '#9e9e9e',
    fontSize: '12px',
  },
}));

const SingleSelectDropdown = ({
  options = [],
  value = '',
  onChange,
  searchEnabled = true,
  placeholder = 'Select an option',
  searchQuery = '',
  onSearchQueryChange = () => {},
  disabled = false,
  returnType = 'string', // 'string' or 'object'
   displayField = null,
width = 180,
height = 20,
}) => {
  const classes = useStyles({ width, height });
  const [open, setOpen] = React.useState(false);

  const menuProps = {
  ...MenuProps,
  PaperProps: {
    ...MenuProps.PaperProps,
    style: {
      ...MenuProps.PaperProps.style,
      width,
    },
  },
};

  const isObjectOptions = React.useMemo(
    () => typeof options?.[0] === 'object',
    [options]
  );

  // 🧠 SMART: detect best label key
 const getDisplayField = (obj) => {
  if (!obj || typeof obj !== 'object') return '';
// If displayField is explicitly provided
  if (displayField && obj[displayField]) {
    return obj[displayField];
  }
  const priorityKeys = ['Customer', 'Servicedesk', 'Language', 'AccountName', 'Name'];
  const match = priorityKeys.find((key) => typeof obj[key] === 'string' && obj[key].trim() !== '');
  if (match) return obj[match];
  const firstStringKey = Object.keys(obj).find((k) => typeof obj[k] === 'string');
  return firstStringKey ? obj[firstStringKey] : '';
};

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    let selectedOption;

    if (isObjectOptions) {
      selectedOption = options.find((opt) => getDisplayField(opt) === selectedValue);
    } else {
      selectedOption = selectedValue;
    }

    if (returnType === 'object' && isObjectOptions) {
      onChange(selectedOption || null);
    } else {
      onChange(isObjectOptions ? getDisplayField(selectedOption) : selectedValue);
    }
  };

  const handleMenuOpen = () => !disabled && setOpen(true);
  const handleMenuClose = () => setOpen(false);
  const handleSearchKeyDown = (event) => event.stopPropagation();

  const filteredOptions = options.filter((option) => {
    const label = isObjectOptions ? getDisplayField(option) : option || '';
    return label.toLowerCase().includes(searchQuery.toLowerCase());
  });

  React.useEffect(() => {
    if (filteredOptions.length === 1 && !value) {
      const onlyOption = filteredOptions[0];
      if (returnType === 'object' && isObjectOptions) onChange(onlyOption);
      else onChange(isObjectOptions ? getDisplayField(onlyOption) : onlyOption);
    }
  }, [filteredOptions, value, onChange, isObjectOptions, returnType]);

  const getDisplayLabel = () => {
    if (!value) return '';
    if (isObjectOptions) return getDisplayField(value);
    return value;
  };

  return (
    <FormControl className={classes.formControl} size="small" disabled={disabled}>
      <Select
        value={
          !value
            ? ''
            : isObjectOptions
            ? getDisplayField(value)
            : typeof value === 'string'
            ? value
            : ''
        }
        onChange={handleChange}
        input={<OutlinedInput className={classes.input} />}
        MenuProps={menuProps}
        open={open}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        displayEmpty
        renderValue={(selected) => {
          if (!selected)
            return <Typography className={classes.placeholder}>{placeholder}</Typography>;

          let label = '';
          if (typeof selected === 'object') {
            label = getDisplayField(selected);
          } else if (isObjectOptions) {
            label = getDisplayField(value);
          } else {
            label = selected;
          }

          return <Typography style={{ fontSize: '12px' }}>{label || placeholder}</Typography>;
        }}
      >
        {searchEnabled && (
          <ListSubheader>
            <TextField
              fullWidth
              value={searchQuery}
              onChange={onSearchQueryChange}
              placeholder="Search..."
              variant="outlined"
              size="small"
              autoFocus
              onKeyDown={handleSearchKeyDown}
              disabled={disabled}
              InputProps={{
                style: { height: 26, fontSize: '12px', padding: '2px 6px' },
              }}
            />
          </ListSubheader>
        )}

        {filteredOptions.length === 0 ? (
          <MenuItem disabled>No results</MenuItem>
        ) : (
          filteredOptions.map((option, idx) => {
            const label = isObjectOptions ? getDisplayField(option) : option;
            return (
              <MenuItem key={idx} value={label} className={classes.menuItem}>
                {label}
              </MenuItem>
            );
          })
        )}
      </Select>
    </FormControl>
  );
};

export default SingleSelectDropdown;
